/**
 * Dropdown Menu Rendering
 * Handles HTML generation, display and translation for dropdown menus
 */

import { Roles } from "../../constants/roles";
import { phpAuthUser } from "../../core/php-data-access";

interface DropdownMenuItem {
    action: string;
    icon: string;
    label: string;
    translateKey: string;
    fallbackText: string;
    requiresOwnership: boolean;
    show: boolean;
    dangerous?: boolean;
    warning?: boolean;
}

interface DropdownEntity {
    id?: number;
    post_id?: number;
    comment_id?: number;
    user_id?: number | null;
}

class DropdownRender {
    public generatePostDropdownMenu(post: DropdownEntity): string {
        const authUser = phpAuthUser();
        const isAuthenticated = authUser !== null;

        if (!isAuthenticated) {
            return "";
        }

        const currentUserId = authUser.user_id || authUser.id;
        const isAdmin = Boolean(authUser.is_admin) || authUser.role === "admin";
        const canEdit = currentUserId == post.user_id || isAdmin;

        const menuItems: DropdownMenuItem[] = [
            {
                action: "edit",
                icon: "bi-pencil",
                label: "edit",
                translateKey: "edit",
                fallbackText: "แก้ไข",
                requiresOwnership: true,
                show: canEdit,
            },
            {
                action: "delete",
                icon: "bi-trash",
                label: "delete",
                translateKey: "delete",
                fallbackText: "ลบ",
                requiresOwnership: true,
                dangerous: true,
                show: canEdit,
            },
            {
                action: "share",
                icon: "bi-share",
                label: "share",
                translateKey: "share",
                fallbackText: "แชร์",
                requiresOwnership: false,
                show: true,
            },
            {
                action: "report",
                icon: "bi-flag",
                label: "report",
                translateKey: "report",
                fallbackText: "รายงาน",
                requiresOwnership: false,
                warning: true,
                show: true,
            },
        ];

        const visibleItems = menuItems.filter((item) => item.show);
        if (visibleItems.length === 0) {
            return "";
        }

        const id = post.post_id || post.id;
        const menuItemsHtml = visibleItems
            .map((item) => {
                const classes = ["menu-item", `${item.action}-item`];

                if (item.action === "edit") classes.push("edit-button");
                if (item.action === "delete") classes.push("delete-button");
                if (item.dangerous) classes.push("delete-item");
                if (item.warning) classes.push("report-item");

                return `
                <button type="button" 
                    class="${classes.join(" ")}" 
                    data-role="${
                        item.action === "edit"
                            ? Roles.POST_ACTION_EDIT
                            : item.action === "delete"
                              ? Roles.POST_ACTION_DELETE
                              : item.action === "share"
                                ? Roles.POST_ACTION_SHARE
                                : Roles.DROPDOWN_MENU_ITEM
                    }"
                    data-dropdown-role="${Roles.DROPDOWN_MENU_ITEM}"
                    data-action="${item.action}" 
                    data-post-id="${id}">
                    <i class="${item.icon}"></i>
                    <span data-translate="${item.translateKey}">${item.fallbackText}</span>
                </button>
            `;
            })
            .join("");

        return `
            <div class="post-dropdown">
                <button type="button" class="post-menu-btn" data-role="${Roles.POST_MENU_BUTTON}" data-post-id="${id}">
                    <i class="bi bi-three-dots"></i>
                </button>
                <div class="post-menu" data-role="${Roles.POST_MENU}" id="post-menu-${id}" style="display: none;">
                    ${menuItemsHtml}
                </div>
            </div>
        `;
    }

    public generateCommentDropdownMenu(comment: DropdownEntity): string {
        const authUser = phpAuthUser();
        const isAuthenticated = authUser !== null;

        if (!isAuthenticated) {
            return "";
        }

        const currentUserId = authUser.user_id || authUser.id;
        const isAdmin = Boolean(authUser.is_admin) || authUser.role === "admin";
        const canEdit = currentUserId == comment.user_id || isAdmin;

        const menuItems: DropdownMenuItem[] = [
            {
                action: "edit",
                icon: "bi-pencil",
                label: "edit",
                translateKey: "edit",
                fallbackText: "แก้ไข",
                requiresOwnership: true,
                show: canEdit,
            },
            {
                action: "delete",
                icon: "bi-trash",
                label: "delete",
                translateKey: "delete",
                fallbackText: "ลบ",
                requiresOwnership: true,
                dangerous: true,
                show: canEdit,
            },
            {
                action: "report",
                icon: "bi-flag",
                label: "report",
                translateKey: "report",
                fallbackText: "รายงาน",
                requiresOwnership: false,
                warning: true,
                show: true,
            },
        ];

        const visibleItems = menuItems.filter((item) => item.show);
        if (visibleItems.length === 0) {
            return "";
        }

        const id = comment.comment_id || comment.id;
        const menuItemsHtml = visibleItems
            .map((item) => {
                const classes = ["menu-item", `${item.action}-item`];

                if (item.dangerous) classes.push("delete-item");
                if (item.warning) classes.push("report-item");

                return `
                <button type="button" 
                    class="${classes.join(" ")}" 
                    data-role="${
                        item.action === "edit"
                            ? Roles.COMMENT_ACTION_EDIT
                            : item.action === "delete"
                              ? Roles.COMMENT_ACTION_DELETE
                              : Roles.DROPDOWN_MENU_ITEM
                    }"
                    data-dropdown-role="${Roles.DROPDOWN_MENU_ITEM}"
                    data-action="${item.action}" 
                    data-comment-id="${id}">
                    <i class="${item.icon}"></i>
                    <span data-translate="${item.translateKey}">${item.fallbackText}</span>
                </button>
            `;
            })
            .join("");

        return `
            <div class="comment-dropdown">
                <button type="button" class="comment-menu-btn" data-role="${Roles.COMMENT_MENU_BUTTON}" data-comment-id="${id}">
                    <i class="bi bi-three-dots"></i>
                </button>
                <div class="comment-menu" data-role="${Roles.COMMENT_MENU}" id="comment-menu-${id}" style="display: none;">
                    ${menuItemsHtml}
                </div>
            </div>
        `;
    }
}

const dropdownRender = new DropdownRender();

export { DropdownRender, dropdownRender };
